export const PURCHASE_CONDITION_TYPES = ['purchased_category', 'purchased_product', 'purchased_product_name'];
export const PERIOD_PURCHASE_CONDITION_TYPES = ['purchase_count_within_days', 'purchase_amount_within_days'];
export const PLAIN_NUMERIC_TYPES = [
  'registration_days',
  'total_purchase_count',
  'total_purchase_amount',
  'last_purchase_days',
  'age',
  'available_points'
];
export const CHOICE_TYPES = ['member_tier', 'gender', 'birth_month', 'location'];

export const CONDITION_LABELS = {
  registration_days: '註冊天數',
  total_purchase_count: '總消費次數',
  total_purchase_amount: '總消費金額',
  last_purchase_days: '最後消費天數',
  purchase_count_within_days: '時間內交易次數',
  purchase_amount_within_days: '時間內交易金額',
  member_tier: '會員等級',
  age: '年齡',
  gender: '性別',
  birth_month: '生日月份',
  tag_exists: '標籤',
  purchased_category: '購買品類',
  purchased_product: '購買商品',
  purchased_product_name: '購買品名關鍵字',
  available_points: '點數',
  location: '居住地區',
  registration_date: '註冊日期'
};

const OPERATOR_LABELS = {
  equals: '等於',
  not_equals: '不等於',
  greater_than: '大於',
  greater_or_equal: '大於等於',
  less_than: '小於',
  less_or_equal: '小於等於',
  between: '介於',
  exists: '擁有',
  not_exists: '沒有',
  in: '為',
  not_in: '不為'
};

const GENDER_LABELS = { male: '男', female: '女', neutral: '中性', unknown: '未設定' };

export const TAIWAN_CITIES = [
  '台北市', '新北市', '桃園市', '台中市', '台南市', '高雄市',
  '基隆市', '新竹市', '嘉義市',
  '新竹縣', '苗栗縣', '彰化縣', '南投縣', '雲林縣', '嘉義縣',
  '屏東縣', '宜蘭縣', '花蓮縣', '台東縣', '澎湖縣', '金門縣', '連江縣'
];

export function createConditionId() {
  return `cond_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

export function createEmptyCondition() {
  return {
    id: createConditionId(),
    conditionType: '',
    operator: '',
    value: ''
  };
}

export function createEmptyGroup() {
  return {
    logicalOperator: 'AND',
    conditions: [createEmptyCondition()]
  };
}

export function createPurchaseConditionValue(conditionType) {
  if (conditionType === 'purchased_category') {
    return { categoryId: '', minCount: 1, withinDays: null };
  }
  if (conditionType === 'purchased_product_name') {
    return { keywords: [], keywordsText: '', minCount: 1, withinDays: null };
  }
  return { productId: '', productName: '', minCount: 1, withinDays: null };
}

export function createPeriodPurchaseValue() {
  return {
    withinDays: 30,
    threshold: null,
    min: null,
    max: null
  };
}

export function parseKeywordsText(text) {
  const list = String(text || '')
    .split(/[,，、]/)
    .map(keyword => keyword.trim())
    .filter(Boolean);
  return [...new Set(list)];
}

export function hydrateConditionGroups(groups) {
  (groups || []).forEach(group => {
    (group.conditions || []).forEach(condition => {
      if (PURCHASE_CONDITION_TYPES.includes(condition.conditionType)) {
        const current = condition.value && typeof condition.value === 'object' ? condition.value : {};
        condition.value = {
          ...createPurchaseConditionValue(condition.conditionType),
          ...current
        };
        if (condition.conditionType === 'purchased_product_name' && !condition.value.keywordsText) {
          condition.value.keywordsText = (condition.value.keywords || []).join(', ');
        }
      }
      if (PERIOD_PURCHASE_CONDITION_TYPES.includes(condition.conditionType)) {
        condition.value = {
          ...createPeriodPurchaseValue(),
          ...(condition.value && typeof condition.value === 'object' ? condition.value : {})
        };
      }
    });
  });
  return groups;
}

function isBlank(value) {
  return value === null || value === undefined || value === '';
}

function isValidMetric(value, { integer }) {
  if (isBlank(value)) return false;
  const number = Number(value);
  if (!Number.isFinite(number) || number < 0) return false;
  if (integer && !Number.isInteger(number)) return false;
  return true;
}

/**
 * 檢查條件組並把品名關鍵字寫回 keywords。
 * @returns {string|null} 錯誤訊息
 */
export function validateConditionGroups(groups) {
  if (!groups || groups.length === 0) {
    return '請至少新增一個條件組';
  }

  for (let groupIndex = 0; groupIndex < groups.length; groupIndex++) {
    const group = groups[groupIndex];
    if (!group.conditions || group.conditions.length === 0) {
      return `條件組 ${groupIndex + 1} 至少需要一個條件`;
    }

    for (const condition of group.conditions) {
      const label = CONDITION_LABELS[condition.conditionType] || '條件';
      if (!condition.conditionType || !condition.operator || isBlank(condition.value)) {
        return '請完整填寫所有條件';
      }
      if (Array.isArray(condition.value) && condition.value.length === 0) {
        return `請完整填寫「${label}」條件`;
      }
      if (condition.conditionType === 'purchased_category' && !condition.value.categoryId) {
        return '「購買品類」條件請選擇品類';
      }
      if (condition.conditionType === 'purchased_product' && !condition.value.productId) {
        return '「購買商品」條件請選擇商品';
      }
      if (condition.conditionType === 'purchased_product_name') {
        condition.value.keywords = parseKeywordsText(condition.value.keywordsText);
        if (condition.value.keywords.length === 0) {
          return '「購買品名關鍵字」條件請至少輸入一個關鍵字';
        }
        if (condition.value.keywords.length > 20) {
          return '品名關鍵字最多 20 個';
        }
        if (condition.value.keywords.some(keyword => keyword.length > 50)) {
          return '單一品名關鍵字不能超過 50 個字元';
        }
      }
      if (PURCHASE_CONDITION_TYPES.includes(condition.conditionType)) {
        const minCount = condition.value.minCount;
        if (!isBlank(minCount) && (!Number.isInteger(Number(minCount)) || Number(minCount) < 1)) {
          return '購買次數門檻必須是大於等於 1 的整數';
        }
        const withinDays = condition.value.withinDays;
        if (!isBlank(withinDays) && (!Number.isInteger(Number(withinDays)) || Number(withinDays) < 1)) {
          return '時間範圍（天）必須是正整數';
        }
      }
      if (PERIOD_PURCHASE_CONDITION_TYPES.includes(condition.conditionType)) {
        const isAmount = condition.conditionType === 'purchase_amount_within_days';
        const unit = isAmount ? '金額' : '次數';
        const numberRule = { integer: !isAmount };
        if (!Number.isInteger(Number(condition.value?.withinDays)) || Number(condition.value.withinDays) < 1) {
          return `「${label}」的天數必須是正整數`;
        }
        if (condition.operator === 'between') {
          if (!isValidMetric(condition.value.min, numberRule) || !isValidMetric(condition.value.max, numberRule)) {
            return `「${label}」的${unit}上下限必須是${isAmount ? '大於等於 0 的數字' : '大於等於 0 的整數'}`;
          }
          if (Number(condition.value.min) > Number(condition.value.max)) {
            return `「${label}」的下限不能大於上限`;
          }
        } else if (!isValidMetric(condition.value.threshold, numberRule)) {
          return `「${label}」的${unit}必須是${isAmount ? '大於等於 0 的數字' : '大於等於 0 的整數'}`;
        }
      }
      if (PLAIN_NUMERIC_TYPES.includes(condition.conditionType)) {
        const integer = condition.conditionType !== 'total_purchase_amount';
        const numberRule = { integer };
        const unitHint = integer ? '整數' : '數字';
        if (condition.operator === 'between') {
          if (!condition.value || typeof condition.value !== 'object' || !isValidMetric(condition.value.min, numberRule) || !isValidMetric(condition.value.max, numberRule)) {
            return `「${label}」的上下限必須是大於等於 0 的${unitHint}`;
          }
          if (Number(condition.value.min) > Number(condition.value.max)) {
            return `「${label}」的下限不能大於上限`;
          }
        } else if (!isValidMetric(condition.value, numberRule)) {
          return `「${label}」必須是大於等於 0 的${unitHint}`;
        }
      }
      if (condition.conditionType === 'registration_date') {
        if (condition.operator === 'between') {
          if (!condition.value?.start || !condition.value?.end) {
            return '「註冊日期」請填寫開始與結束日期';
          }
          if (condition.value.start > condition.value.end) {
            return '「註冊日期」的開始日期不能晚於結束日期';
          }
        } else if (isBlank(condition.value) || typeof condition.value === 'object') {
          return '「註冊日期」請填寫日期';
        }
      }
    }
  }

  return null;
}

function formatValue(condition, labels = {}) {
  const { conditionType, operator, value } = condition;
  if (conditionType === 'member_tier' && labels.member_tier) {
    const mapOne = (item) => labels.member_tier[item] || item;
    return Array.isArray(value) ? value.map(mapOne).join('、') : mapOne(value);
  }
  if (conditionType === 'gender') {
    const mapOne = (item) => GENDER_LABELS[item] || item;
    return Array.isArray(value) ? value.map(mapOne).join('、') : mapOne(value);
  }
  if (conditionType === 'birth_month') {
    const mapOne = (item) => `${item} 月`;
    return Array.isArray(value) ? value.map(mapOne).join('、') : mapOne(value);
  }
  if (conditionType === 'registration_date') {
    if (operator === 'between') return `${value.start} ~ ${value.end}`;
    return value;
  }
  if (PLAIN_NUMERIC_TYPES.includes(conditionType) && operator === 'between' && value && typeof value === 'object') {
    return `${value.min} ~ ${value.max}`;
  }
  if (PERIOD_PURCHASE_CONDITION_TYPES.includes(conditionType) && value && typeof value === 'object') {
    const unit = conditionType === 'purchase_amount_within_days' ? '元' : '次';
    const metric = operator === 'between'
      ? (!isBlank(value.min) && !isBlank(value.max) ? `${value.min} ~ ${value.max}` : '')
      : (!isBlank(value.threshold) ? value.threshold : '');
    return metric === '' ? `近 ${value.withinDays} 天` : `近 ${value.withinDays} 天 ${metric}${unit}`;
  }
  if (conditionType === 'purchased_category') return '指定品類';
  if (conditionType === 'purchased_product') return value?.productName || '指定商品';
  if (conditionType === 'purchased_product_name') {
    return (value?.keywords || []).join('、') || value?.keywordsText || '';
  }
  if (Array.isArray(value)) return value.join('、');
  return value;
}

export function describeConditionGroups(groups, labels = {}) {
  const parts = (groups || []).map(group => {
    const joiner = group.logicalOperator === 'OR' ? ' 或 ' : ' 且 ';
    const texts = (group.conditions || [])
      .filter(condition => condition.conditionType)
      .map(condition => {
        const label = CONDITION_LABELS[condition.conditionType] || condition.conditionType;
        const operator = OPERATOR_LABELS[condition.operator] || condition.operator || '';
        const formatted = formatValue(condition, labels);
        return [label, operator, formatted].filter(Boolean).join(' ');
      })
      .filter(Boolean);
    if (texts.length === 0) return '';
    return texts.join(joiner);
  }).filter(Boolean);

  if (parts.length === 0) return '';
  if (parts.length === 1) return `符合以下條件的會員：${parts[0]}`;
  return `符合以下任一條件組的會員：${parts.join('；或 ')}`;
}

function newCondition(partial) {
  return {
    id: createConditionId(),
    conditionType: '',
    operator: '',
    value: '',
    ...partial
  };
}

function convertLegacyRule(rule) {
  const params = rule.params || {};
  switch (rule.type) {
    case 'tag': {
      const negative = params.matchType === 'none' || params.matchType === 'not_all';
      return (params.tags || []).map(tagName => newCondition({
        conditionType: 'tag_exists',
        operator: negative ? 'not_exists' : 'exists',
        value: tagName
      }));
    }
    case 'member_tier': {
      const tiers = params.tiers || [];
      if (tiers.length === 1) {
        return [newCondition({ conditionType: 'member_tier', operator: 'equals', value: tiers[0] })];
      }
      if (tiers.length > 1) {
        return [newCondition({ conditionType: 'member_tier', operator: 'in', value: tiers })];
      }
      return [];
    }
    case 'points_range': {
      const hasMin = !isBlank(params.min);
      const hasMax = !isBlank(params.max);
      if (hasMin && hasMax) {
        return [newCondition({ conditionType: 'available_points', operator: 'between', value: { min: params.min, max: params.max } })];
      }
      if (hasMin) return [newCondition({ conditionType: 'available_points', operator: 'greater_or_equal', value: params.min })];
      if (hasMax) return [newCondition({ conditionType: 'available_points', operator: 'less_or_equal', value: params.max })];
      return [];
    }
    case 'demographics': {
      const conditions = [];
      const genders = params.gender || [];
      if (genders.length === 1) conditions.push(newCondition({ conditionType: 'gender', operator: 'equals', value: genders[0] }));
      else if (genders.length > 1) conditions.push(newCondition({ conditionType: 'gender', operator: 'in', value: genders }));
      const hasMin = !isBlank(params.ageRange?.min);
      const hasMax = !isBlank(params.ageRange?.max);
      if (hasMin && hasMax) {
        conditions.push(newCondition({ conditionType: 'age', operator: 'between', value: { min: params.ageRange.min, max: params.ageRange.max } }));
      } else if (hasMin) {
        conditions.push(newCondition({ conditionType: 'age', operator: 'greater_or_equal', value: params.ageRange.min }));
      } else if (hasMax) {
        conditions.push(newCondition({ conditionType: 'age', operator: 'less_or_equal', value: params.ageRange.max }));
      }
      return conditions;
    }
    case 'location': {
      const cities = params.cities || [];
      if (cities.length === 1) return [newCondition({ conditionType: 'location', operator: 'equals', value: cities[0] })];
      if (cities.length > 1) return [newCondition({ conditionType: 'location', operator: 'in', value: cities })];
      return [];
    }
    case 'registration_date': {
      if (!isBlank(params.relativeDays)) {
        return [newCondition({ conditionType: 'registration_days', operator: 'less_or_equal', value: params.relativeDays })];
      }
      const start = params.dateRange?.start;
      const end = params.dateRange?.end;
      if (start && end) return [newCondition({ conditionType: 'registration_date', operator: 'between', value: { start, end } })];
      if (start) return [newCondition({ conditionType: 'registration_date', operator: 'greater_or_equal', value: start })];
      if (end) return [newCondition({ conditionType: 'registration_date', operator: 'less_or_equal', value: end })];
      return [];
    }
    default:
      return [];
  }
}

export function legacyRulesToConditionGroups(rulesConfig) {
  const rules = rulesConfig?.rules || [];
  let logicalOperator = rulesConfig?.logic === 'OR' ? 'OR' : 'AND';
  if (rules.length === 1 && rules[0].type === 'tag') {
    const matchType = rules[0].params?.matchType || 'any';
    logicalOperator = (matchType === 'any' || matchType === 'not_all') ? 'OR' : 'AND';
  }
  const conditions = rules.flatMap(convertLegacyRule);
  if (conditions.length === 0) return [createEmptyGroup()];
  return [{ logicalOperator, conditions }];
}
